import type { AppType } from "@shelflist/server";
import { type ApiErrorCode, isApiErrorBody, isApiErrorCode } from "@shelflist/shared";
import type { ClientResponse } from "hono/client";
import { hc } from "hono/client";
import type { SuccessStatusCode } from "hono/utils/http-status";

/**
 * Typed RPC client for the Shelflist API (end-to-end types from the server's
 * route definitions, no code generation). Use together with {@link call}.
 */
export const api = hc<AppType>("/", { init: { credentials: "same-origin" } }).api;

/** Error thrown for every non-2xx API response; carries the machine-readable code. */
export class ApiError extends Error {
  override name = "ApiError";

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly details: Record<string, string> = {},
  ) {
    super(message);
  }
}

export async function toApiError(res: Response): Promise<ApiError> {
  const body: unknown = await res.json().catch(() => undefined);
  if (isApiErrorBody(body)) {
    const code = isApiErrorCode(body.error.code) ? body.error.code : "internal_error";
    return new ApiError(
      res.status,
      code,
      body.error.message,
      body.error.fields,
      // Values are strings except numeric ones such as `limit` (read with Number()).
      body.error.details as Record<string, string> | undefined,
    );
  }
  const code: ApiErrorCode =
    res.status === 401
      ? "unauthorized"
      : res.status === 429
        ? "too_many_requests"
        : "internal_error";
  return new ApiError(res.status, code, res.statusText);
}

type SuccessBody<R> =
  R extends ClientResponse<infer T, infer S, infer _F>
    ? S extends SuccessStatusCode
      ? S extends 204
        ? undefined
        : T
      : never
    : never;

/**
 * Awaits an RPC request and returns the typed success body, or throws {@link ApiError}.
 *
 * @example const me = await call(api.me.$get());
 */
export async function call<R extends ClientResponse<unknown, number, string>>(
  request: Promise<R>,
): Promise<SuccessBody<R>> {
  let res: R;
  try {
    res = await request;
  } catch {
    throw new ApiError(0, "service_unavailable", "Network error");
  }
  if (!res.ok) throw await toApiError(res as unknown as Response);
  if (res.status === 204) return undefined as SuccessBody<R>;
  return (await res.json()) as SuccessBody<R>;
}

/**
 * Better Auth endpoints are mounted as a raw handler on the server and therefore
 * not part of the RPC types; they are called with plain fetch.
 */
async function authPost(path: string, body: unknown): Promise<void> {
  const res = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
}

export const authApi = {
  signIn: (input: { email: string; password: string }) =>
    authPost("/sign-in/email", { ...input, rememberMe: true }),
  signOut: () => authPost("/sign-out", {}),
};
